import React, {
    Dispatch, SetStateAction, useRef, useState, useContext,
} from 'react';
import {
    Box, Typography, DialogContent, DialogTitle,
    Dialog, Grid, Button, IconButton, FormControlLabel, Checkbox,
} from '@mui/material';
import downloadTempIcon from '../../../../assets/downloadTempIcon.svg';
import uploadExcelIcon from '../../../../assets/uploadExcelIcon.svg';
import CloseIcon from '@mui/icons-material/Close';
import ImportOptionsModelStyle from './ImportOptionsModel.style';
import NotificationBar from '../../../../components/NotificationBar';
import NotificationBarType from '../../../../types/NotificationBarType';
import Loader from '../../../../components/Loader';
import { useOutletContext } from 'react-router-dom';
import { OutletContext } from '../../../../types/OrganisationDetailType';
import { UserContext } from '../../../../store/context/userContext';
import { getAccessTokenFromLocalStorage } from '../../../../helper/StorageHelper';
import { OrganisationContext } from '../../../../store/context/organisationContext';

const importUrl = process.env.REACT_APP_DATA_IMPORT_ENDPOINT || '';
interface InviteProps {
    open: boolean,
    setOpenImport: Dispatch<SetStateAction<boolean>>,
    setSearchText?: Dispatch<SetStateAction<string>>,
    projectId?: string,
    refetchProjInfo?: ({ orgId }?: { [key: string]: string | number }) => void,
    refetchVersion?: ({ orgId }?: { [key: string]: string | number }) => void,
    refetchPhase?: ({ orgId }?: { [key: string]: string | number }) => void,
    refetchCase?: ({ orgId }?: { [key: string]: string | number }) => void,
    refetchProject?: ({ orgId }?: { [key: string]: string | number }) => void,
    refetchIndustries?: ({ orgId }?: { [key: string]: string | number }) => void,
    refetchCommodities?: ({ orgId }?: { [key: string]: string | number }) => void,
    isNewProjectVersion?: boolean,
}

function ImportOptModel({
    open, setOpenImport, projectId, refetchProjInfo, refetchVersion, refetchPhase,
    refetchCase, refetchProject, refetchIndustries, refetchCommodities, isNewProjectVersion, setSearchText,
}: InviteProps) {
    const cont: OutletContext = useOutletContext();
    const classes = ImportOptionsModelStyle();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [notificationDetails, setNotificationDetails] = useState<NotificationBarType>({
        open: false,
        type: 'success',
        message: '',
    });
    const [isloading, setLoading] = useState<boolean>(false);
    const [isSheetValidated, setChecked] = React.useState(false);

    const OrganisationCtx = useContext(OrganisationContext);
    const fillerFn = () => {};
    const refetchOrg = OrganisationCtx?.refetchOrgCost || fillerFn;
    const refetchProjCount = OrganisationCtx?.refetchProjCount || fillerFn;

    // finding orgId
    const userContext = useContext(UserContext);
    const orgId = userContext?.user?.default_org_id || '';

    const handleClose = () => {
        setOpenImport(false);
    };

    const handleDownload = () => {
        if (refetchIndustries || refetchCommodities) {
            window.open('GCOA&GWBS-Sample.xlsx', '_blank');
        } else {
            window.open('./Project Sample.xlsx', '_blank');
        }
    };

    const handleUpload = () => {
        // Trigger the file input click programmatically
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // --------------------For Uploading excel file------------------------------------------------
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const accessToken = getAccessTokenFromLocalStorage();
        const selectedFile = event.target.files && event.target.files[0];
        if (selectedFile) {
            const formData = new FormData();
            formData.append('multipartFile', selectedFile);
            formData.append('activeOrgId', orgId);
            formData.append('isSheetValidated', isSheetValidated.toString());
            // sending activeProjectId only when uploading new version of project
            if (isNewProjectVersion) formData.append('activeProjectId', projectId || '');
            setLoading(true);
            let url = `${importUrl}/spring/pcu/import-excel`;
            if (refetchIndustries || refetchCommodities) {
                url = `${importUrl}/spring/pcu/import-gCoaNgWbs`;
            }
            fetch(url, {
                method: 'POST',
                body: formData,
                headers: {
                    Authorization: `Bearer ${accessToken || ''}`,
                },
            }).then(async (res) => ({
                err: !res.ok,
                data: await res.text(),
                isUnauthorised: res.status === 401 || res.status === 403,
            })).then((data) => {
                const message = data.isUnauthorised ? 'User is unauthorised' : data.data;
                cont.showNotificationBar(message, data.err ? 'error' : 'success');
                if (!data.err) {
                    setOpenImport(false);
                    // refetch Total project count and total value
                    refetchOrg();
                    refetchProjCount();
                    // refetch Case, Phase, Version and Project info api after importing new version of a project from project setting page
                    if (refetchProjInfo) refetchProjInfo();
                    if (refetchVersion) refetchVersion();
                    if (refetchPhase) refetchPhase();
                    if (refetchCase) refetchCase();
                    // refetch projects after importing project from project listing page
                    if (refetchProject) refetchProject();
                    if (refetchIndustries) refetchIndustries();
                    if (refetchCommodities) refetchCommodities();
                    if (setSearchText) setSearchText('');
                }
            }).catch((error) => {
                if (error instanceof Error) {
                    setNotificationDetails({
                        open: true,
                        message: `Error during file upload: ${error.message}`,
                        type: 'error',
                    });
                } else {
                    setNotificationDetails({
                        open: true,
                        message: `Unexpected error during file upload: ${String(error)}`,
                        type: 'error',
                    });
                }
            })
                .finally(() => {
                    setLoading(false);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                });
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setChecked(event.target.checked);
    };

    return (
        <Box>
            <Dialog fullWidth className={classes.mainDilogBoxSection} open={open}>
                <Loader loading={isloading} />
                <DialogTitle className={classes.dilogBoxSection}>Import Options</DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                    disabled={isloading}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent className={classes.dialogBody}>
                    <Grid container className={classes.sectionBody}>
                        <Grid item xs={6} className={classes.leftdivCenter}>
                            <img src={downloadTempIcon} alt="graph" />
                            {refetchCommodities || refetchIndustries
                                ? (<Typography>Download the full Generic WBS & Generic COA import template in Excel format.</Typography>)
                                : (<Typography>Download the full project import template in Excel format.</Typography>)}
                            <Button
                                variant="outlined"
                                onClick={handleDownload}
                            >
                                Download Template

                            </Button>
                        </Grid>
                        <Grid item xs={6} className={classes.rigntdivCenter}>
                            <img src={uploadExcelIcon} alt="graph" />
                            {refetchCommodities || refetchIndustries
                                ? (
                                    <Typography>
                                        Upload a complete Generic WBS & Generic COA import template in Excel format.
                                    </Typography>
                                )
                                : (<Typography>Upload a complete project that follows the full project import template in Excel format.</Typography>)}
                            <Box className={classes.validatedStyle}>
                                <Box className={classes.checkboxStyle}>
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                checked={isSheetValidated}
                                                onChange={handleChange}
                                            />
                                        )}
                                        label="Is sheet validated?"
                                    />
                                </Box>
                                <Box>
                                    <Button variant="contained" color="primary" onClick={handleUpload}>
                                        Upload .excel file
                                    </Button>
                                </Box>
                            </Box>
                            {/* Hidden file input for triggering file selection */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xls,.xlsx"
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
            <NotificationBar
                open={notificationDetails.open}
                setOpen={(newState) => setNotificationDetails({ ...notificationDetails, open: newState })}
                type={notificationDetails.type}
                message={notificationDetails.message}
            />
        </Box>
    );
}

export default ImportOptModel;
